import { Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { createId } from '@core/util/file';
import { DATE_FORMAT } from '@models/businesses';
import {
	DefaultMatterAdjustmentDeposit,
	DefaultMatterAdjustmentDepositRecord,
	DepositRecord,
	MatterAdjustmentDeposit,
} from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { AdjustmentDefaultDetailsSelectors } from '@state/global/adjustment-details';
import { AdjustmentsDispatch } from '@state/matter/adjustments';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { format } from 'date-fns';
import { cloneDeep } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { ICON } from '@core/util/icon';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { TestHooks } from './deposit-side-form.hooks';

@Component({
	selector: 'app-deposit-side-form',
	templateUrl: 'deposit-side-form.component.html',
	standalone: false,
})
@UntilDestroy()
export class DepositSideFormComponent {
	testHooks = TestHooks;
	adjustment: MatterAdjustmentDeposit = {
		...cloneDeep(DefaultMatterAdjustmentDeposit),
		id: createId(),
	};
	@SelectSnapshot(AdjustmentDefaultDetailsSelectors.adjustmentDetailsDeposit)
	public adjustmentDetails: boolean;
	agreementDate: string;
	@Select(SelectedMatter.agreementDate) agreementDate$: Observable<string>;
	@Select(AdjustmentsSelector.depositAdjustment)
	depositAdjustment$: Observable<MatterAdjustmentDeposit>;
	readonly ICON = ICON;
	total: number;

	@ViewChild('depositList') private depositList: ElementRef;

	constructor(
		private adjustmentDispatch: AdjustmentsDispatch,
		private navigationService: NavigationService,
		private route: ActivatedRoute,
	) {
		this.total = 0;
		combineLatest([this.depositAdjustment$, this.agreementDate$])
			.pipe(untilDestroyed(this))
			.subscribe(([depositAdjustment, agreementDate]) => {
				this.agreementDate = agreementDate;

				if (depositAdjustment) {
					this.adjustment = cloneDeep({
						...depositAdjustment,
						details: this.adjustmentDetails,
					});
				}

				if (this.adjustment?.deposits?.length === 0) {
					this.addDeposit();
				} else {
					this.scrollToLastDeposit();
				}
			});
	}

	addDeposit(): void {
		this.adjustment.deposits.push({
			...DefaultMatterAdjustmentDepositRecord,
			id: createId(),
			date: this.agreementDate ?? format(new Date(), DATE_FORMAT),
		});
		this.scrollToLastDeposit();
	}

	cancelModal(): void {
		this.navigationService.goBack(this.route);
	}

	changed(): void {
		this.total =
			this.adjustment.deposits.reduce((prev, cur) => +prev + +cur.amount, 0) *
			-1;
	}

	removeDeposit(index: number): void {
		this.adjustment.deposits.splice(index, 1);
	}

	saveModal(): void {
		this.adjustmentDispatch.Set(this.adjustment);
		this.navigationService.goBack(this.route);
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	depositCompoundIndex(index: number, deposit: DepositRecord): number {
		return index;
	}

	private scrollToLastDeposit(): void {
		window.setTimeout(
			() =>
				(this.depositList.nativeElement.scrollTop =
					this.depositList.nativeElement.scrollHeight + 250),
			100,
		);
	}
}
